/**
 * Candid payer-directory search for {@link SearchableAsyncSelect} on the
 * Auth Letter Upload form.
 *
 * Debounce is owned by the select (`debounceMs={500}`). `onSearch` must
 * return options, so this hook calls {@link CandidService.getAllPayers}
 * and maps rows instead of using fire-and-forget {@link useDebouncedApiCall}.
 *
 * Each new search aborts the previous in-flight request via {@link AbortController}.
 * {@link SearchableAsyncSelect} ignores superseded `onSearch` settlements, so an
 * `AbortError` from a cancelled call does not wipe newer options.
 *
 * @module useDebouncedCandidPayers
 */
import { getCurrentFacilityId } from "@risalabs_frontend_org/auth-wrapper";
import { SearchableAsyncSelectOption } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef } from "react";
import { CandidService } from "../../../../api/services/candid/candidService";

/**
 * Result surface for {@link useDebouncedCandidPayers}.
 */
export interface UseDebouncedCandidPayersReturn {
  /**
   * Looks up Candid payers for the current facility and maps them to select options.
   *
   * @param query - Payer-name filter passed through as `search_term`
   * @returns Options with `value` = `payer_id` and `label` = `payer_name`
   */
  searchPayers: (query: string) => Promise<SearchableAsyncSelectOption[]>;
}

/**
 * Builds an abortable `onSearch` handler for the payer {@link SearchableAsyncSelect}.
 *
 * @returns `searchPayers` matching `SearchableAsyncSelectProps.onSearch`
 */
export const useDebouncedCandidPayers = (): UseDebouncedCandidPayersReturn => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const searchPayers = useCallback(
    async (query: string): Promise<SearchableAsyncSelectOption[]> => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const orgId = getCurrentFacilityId() || "";
      const payers = await CandidService.getAllPayers(
        {
          org_id: orgId,
          search_term: query,
        },
        controller.signal,
      );
      return payers.map((payer) => ({
        value: payer.payer_id,
        label: payer.payer_name,
      }));
    },
    [],
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return { searchPayers };
};
